import { Component, OnInit, inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, takeUntil, take } from 'rxjs/operators';
import { Subject, from } from 'rxjs';
import { AuthService } from './auth.service';
import { ToastComponent } from './components/toast/toast.component';
import { NetworkBannerComponent } from './components/network-banner/network-banner.component';
import { ToastService } from './services/toast.service';
import { RfpService } from './services/rfp.service';
import { SessionRealtimeService } from './services/session-realtime.service';
import { UiAccessService } from './services/ui-access.service';
import { PermissionService } from './services/permission.service';
import { extractUserInfo } from './utils/user-claims-extractor';
import { consumeReturnUrl, setReturnUrl } from './utils/return-url';
import { getLoginMode } from './utils/login-mode';
import { isTransientHttpError } from './utils/retry-transient';
import { AuthConfigService } from './auth-config.service';
import {
  resetChunkRecoveryState,
  markAppReady,
  applyPendingDeploymentOnNavigation,
} from './utils/global-error-handler';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastComponent, NetworkBannerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly sessionRealtimeService = inject(SessionRealtimeService);
  private readonly uiAccessService = inject(UiAccessService);
  private readonly toastService = inject(ToastService);
  private readonly permissionService = inject(PermissionService);
  private readonly _destroying$ = new Subject<void>();

  // ── Overlay flags (mutually exclusive via @else if in template) ────────────
  /** Grace period warning: another device logged in, giving user 60s to finish/save */
  showGraceWarning = false;
  graceCountdown = 60;
  private graceInterval: ReturnType<typeof setInterval> | null = null;

  /** Cross-device conflict: another device is already logged in */
  showConflictOverlay = false;
  /** Cross-device terminated: another device force-logged us out */
  showTerminatedOverlay = false;
  terminatedMessage = '';
  /** Same-browser tab conflict: another tab in this browser is active */
  showOtherTabOverlay = false;

  pendingUserDetails: any = null;
  deviceInfo = '';
  ipAddress = '';
  deviceId = '';
  useHereLoading = false;
  useHereError = '';


  /**
   * The auth-response fragment as it stood at bootstrap. Captured here (a field
   * initializer runs at construction, the same point MsalService captures its own copy)
   * because the router's first navigation can replace the URL — and with it the hash —
   * before ngOnInit gets to hand it to MSAL.
   */
  private readonly bootstrapAuthHash = window.location.hash || undefined;

  /**
   * WA-481: the raw query string as it stood at construction (same reason as
   * bootstrapAuthHash — the router's first navigation can strip query params before
   * ngOnInit reads them). The sign-up flow (EmailLoginComponent.onRegister) sends an
   * OAuth `state=signup` which the IdP echoes back here as `?state=signup`. This is the
   * only marker that survives a cross-origin sign-up return (localhost -> dev.wyber.ai),
   * where the per-origin localStorage flag does not.
   */
  private readonly bootstrapAuthSearch = window.location.search || '';

  // ── Tab coordination ────────────────────────────────────────────────────────
  private readonly tabSessionId = crypto.randomUUID();
  private sessionChannel: BroadcastChannel | null = null;
  private isTabLeader = false;
  private tabHeartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatMonitorInterval: ReturnType<typeof setInterval> | null = null;
  private lastHeartbeatFromLeader = 0;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly rfpService: RfpService,
    private readonly authConfigService: AuthConfigService,
  ) {
    this.deviceInfo = this.getDeviceInfo();
    this.rfpService.getIpAddress().subscribe(res => this.ipAddress = res.ip);
    this.deviceId = this.rfpService.getDeviceId();
    sessionStorage.setItem('client_device_id', this.deviceId);

    try {
      this.sessionChannel = new BroadcastChannel('wyber_session_channel');
      this.sessionChannel.onmessage = (event) => this.onChannelMessage(event);
    } catch (e) {
      console.warn('[AppComponent] BroadcastChannel not supported:', e);
    }

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /** Stored so it can be removed in ngOnDestroy (an anonymous listener can't be). */
  private readonly beforeUnloadHandler = (): void => {
    // sessionStorage survives F5 refresh within the same tab but is cleared on
    // tab/browser close — so this flag is present on reload = refresh, absent = close.
    sessionStorage.setItem('_wyber_unloading', 'true');
  };

  // ── Channel message router ──────────────────────────────────────────────────

  private onChannelMessage(event: MessageEvent): void {
    const { type, tabId, newTabId, email } = event.data ?? {};
    const myEmail = sessionStorage.getItem('Email');

    switch (type) {
      // Cross-browser/device takeover (existing behaviour)
      case 'SESSION_TAKEOVER':
        if (newTabId !== this.tabSessionId && email === myEmail) {
          console.warn('[AppComponent] Session taken over by another browser tab');
          this.handleSessionTerminated('Your session was taken over by another window or device.');
        }
        break;

      // Another tab in THIS browser is claiming leadership
      case 'TAB_LEADER_CLAIM':
        if (tabId !== this.tabSessionId && email === myEmail && sessionStorage.getItem('session_token')) {
          this.yieldTabLeadership();
        }
        break;

      // Heartbeat from the current leader tab
      case 'TAB_HEARTBEAT':
        if (tabId !== this.tabSessionId && email === myEmail) {
          this.lastHeartbeatFromLeader = Date.now();
        }
        break;
    }
  }

  // ── Tab leader election ─────────────────────────────────────────────────────

  /**
   * Claim this tab as the active leader.
   * Broadcasts to all other same-browser tabs so they show the inactive overlay.
   */
  private claimTabLeadership(): void {
    const email = sessionStorage.getItem('Email');
    if (!email || !sessionStorage.getItem('session_token')) return;

    this.isTabLeader = true;
    this.showOtherTabOverlay = false;
    this.stopHeartbeatMonitor();
    this.startTabHeartbeat();

    this.sessionChannel?.postMessage({
      type: 'TAB_LEADER_CLAIM',
      tabId: this.tabSessionId,
      email,
      timestamp: Date.now(),
    });
  }

  /** Called when another tab claims leadership — we become inactive */
  private yieldTabLeadership(): void {
    // Single tab restriction disabled per user request to allow multiple tabs in same browser.
    this.showOtherTabOverlay = false;
  }

  /** Active tab sends a heartbeat so inactive tabs know it's still alive */
  private startTabHeartbeat(): void {
    this.stopTabHeartbeat();
    const email = sessionStorage.getItem('Email');
    this.tabHeartbeatInterval = setInterval(() => {
      this.sessionChannel?.postMessage({
        type: 'TAB_HEARTBEAT',
        tabId: this.tabSessionId,
        email,
        timestamp: Date.now(),
      });
    }, 2000);
  }

  private stopTabHeartbeat(): void {
    if (this.tabHeartbeatInterval !== null) {
      clearInterval(this.tabHeartbeatInterval);
      this.tabHeartbeatInterval = null;
    }
  }

  /**
   * Inactive tab watches for heartbeat absence.
   * If the active tab closes/navigates away, we auto-reclaim leadership after ~5 s.
   */
  private startHeartbeatMonitor(): void {
    this.stopHeartbeatMonitor();
    this.lastHeartbeatFromLeader = Date.now(); // assume alive right now

    this.heartbeatMonitorInterval = setInterval(() => {
      const stale = Date.now() - this.lastHeartbeatFromLeader > 5000;
      if (!this.isTabLeader && stale) {
        console.log('[AppComponent] Active tab gone — reclaiming leadership');
        this.stopHeartbeatMonitor();
        this.showOtherTabOverlay = false;
        this.cdr.detectChanges();
        this.claimTabLeadership();
      }
    }, 2000);
  }

  private stopHeartbeatMonitor(): void {
    if (this.heartbeatMonitorInterval !== null) {
      clearInterval(this.heartbeatMonitorInterval);
      this.heartbeatMonitorInterval = null;
    }
  }

  // ── "Use This Tab" handler (other-tab overlay) ─────────────────────────────

  handleUseThisTab(): void {
    this.claimTabLeadership();
    this.cdr.detectChanges();
  }

  // ── Utility ─────────────────────────────────────────────────────────────────

  getDeviceInfo(): string {
    const ua = window.navigator.userAgent;
    let device = 'Unknown Device';
    if (/android/i.test(ua))         device = 'Android Device';
    else if (/iPad|iPhone|iPod/.test(ua)) device = 'iOS Device';
    else if (/Windows/i.test(ua))    device = 'Windows PC';
    else if (/Mac/i.test(ua))        device = 'Mac';
    else if (/Linux/i.test(ua))      device = 'Linux PC';

    let browser = 'Unknown Browser';
    if      (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Chrome')  > -1) browser = 'Chrome';
    else if (ua.indexOf('Safari')  > -1) browser = 'Safari';
    else if (ua.indexOf('Edge')    > -1) browser = 'Edge';

    return `${device} (${browser})`;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // WA-481: a sign-up return carries OAuth `state=signup` in the query string (the
    // manual authorize call uses response_type=code => query response_mode). Promote it
    // to the per-origin localStorage flag now, BEFORE MSAL/bootstrap runs, so whichever
    // createNewUser branch resolves can surface the one-time "account created" toast via
    // showSignupConfirmationIfPending(). This is what makes the toast work when the
    // sign-up redirectUri is a different origin than where registration started.
    try {
      const stateParam = new URLSearchParams(this.bootstrapAuthSearch).get('state');
      if (stateParam === 'signup') {
        sessionStorage.setItem('wyber_pending_signup', '1');
      }
    } catch {
      /* URL parsing/storage unavailable — skip the one-time confirmation */
    }

    // A CIAM sign-up (response_type=code) frequently returns WITHOUT MSAL resolving an
    // account, so the auth guard bounces the user to the public /welcome page and neither
    // createNewUser branch ever runs. The toast lives in AppComponent's template (always
    // mounted, incl. on /welcome), so surface the confirmation here directly, independent
    // of route or login state. showSignupConfirmationIfPending() consumes the flag, so the
    // createNewUser-branch calls below cannot double-fire it on the authenticated path.
    this.showSignupConfirmationIfPending();
    // ── Stale-deployment resilience ──────────────────────────────────────────
    // Proactive: if a newer build is already known to be live, turn this route
    // change into a full document load of the same URL, so the user picks up the
    // new build at a natural boundary instead of hitting a deleted lazy chunk.
    // When nothing is pending this only primes a throttled background check.
    this.router.events.pipe(
      filter((event) => event instanceof NavigationStart),
      takeUntil(this._destroying$)
    ).subscribe((event) => {
      applyPendingDeploymentOnNavigation((event as NavigationStart).url);
    });

    // Reactive: a route rendered, so any earlier chunk recovery is done. Clear the
    // attempt counter on every successful route navigation and strip any temporary
    // cache-busting query parameter so the address bar remains clean.
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntil(this._destroying$)
    ).subscribe((event) => {
      resetChunkRecoveryState();
      markAppReady();

      const navEnd = event as NavigationEnd;
      if (navEnd.url && navEnd.url.includes('_deploy_refresh=')) {
        try {
          const currentUrl = new URL(window.location.href);
          if (currentUrl.searchParams.has('_deploy_refresh')) {
            currentUrl.searchParams.delete('_deploy_refresh');
            window.history.replaceState(null, '', currentUrl.pathname + currentUrl.search + currentUrl.hash);
          }
        } catch {
          // ignore history replace issues
        }
      }
    });

    const terminatedReason = sessionStorage.getItem('session_terminated_reason');
    if (terminatedReason || sessionStorage.getItem('session_terminated') === 'true') {
      this.terminatedMessage = terminatedReason || 'Your session was ended because this account was logged in from another device.';
      this.showTerminatedOverlay = true;
      this.cdr.detectChanges();
    }

    // sessionStorage._wyber_unloading is set on beforeunload and survives F5 refresh
    // (sessionStorage persists across refresh in the same tab) but is gone after a real
    // tab/browser close (sessionStorage is cleared). This reliably distinguishes the two.
    // Deliberately NOT msalService.handleRedirectObservable(): that wrapper can't pass
    // navigateToLoginRequestUrl. Left at its default (true), MSAL navigates the browser
    // back to the page login was started from before exchanging the code — so /welcome
    // re-mounts and paints the marketing landing page for a frame before we route on to
    // /super-admin. That is the login flash. Handling the response in place at the
    // redirectUri removes the extra navigation entirely; the post-login destination is
    // carried in MSAL `state` / consumeReturnUrl() below, never by MSAL's own bounce-back.
    // The trailing resetInProgressEvent() replicates what the wrapper does — without it
    // inProgress$ never reaches None and authGuard blocks forever.
    from(
      this.msalService.instance.initialize()
        .then(() => this.msalService.instance.handleRedirectPromise({
          hash: this.bootstrapAuthHash,
          navigateToLoginRequestUrl: false,
        }))
        .finally(() => this.msalBroadcastService.resetInProgressEvent())
    ).subscribe({
      next: (result) => {
        if (result && result.account) {
          this.msalService.instance.setActiveAccount(result.account);
          // Super Admin (CIAM) accounts don't go through the tenant RFP bootstrap —
          // they use a different Azure identity tenant and have no RFP session/tenant.
          // Token/claims are still stashed in sessionStorage the same way as the tenant
          // flow (minus createNewUser, which is the RFP-tenant registration call).
          if (getLoginMode() === 'super-admin') {
            // clearLoginMode();
            sessionStorage.setItem('access_token', result.idToken);
            const superAdminUserInfo = extractUserInfo(result.account);
            this.createNewSuperAdminUser(result.idTokenClaims);
            sessionStorage.setItem('Email', superAdminUserInfo.email);
            sessionStorage.setItem('UserName', superAdminUserInfo.fullName || superAdminUserInfo.displayName);
            sessionStorage.setItem('isLoggedIn', JSON.stringify(true));
            this.permissionService.setSessionReady(true);
            this.router.navigate(['/super-admin']).catch(e => console.error('[AppComponent] Super Admin redirect failed:', e));
            this.cdr.detectChanges();
            return;
          }

          sessionStorage.setItem('access_token', result.idToken);
          const userInfo = extractUserInfo(result.account);
          sessionStorage.setItem('Email', userInfo.email);
          sessionStorage.setItem('UserName', userInfo.fullName || userInfo.displayName);
          sessionStorage.setItem('isLoggedIn', JSON.stringify(true));
          this.cdr.detectChanges();
          // Wait for createNewUser to store session_token before navigating — otherwise
          // the dashboard mounts and fires its API calls (count/dashboard-summary/etc.)
          // before session_token exists, and they 401 and log the user back out.
          const returnUrl = result.state || consumeReturnUrl();
          void this.createNewUser(result.idTokenClaims).then(() => {
            // WA-481: if this authenticated return follows a sign-up redirect,
            // show an "account created" confirmation toast. The flag is set in
            // EmailLoginComponent.onRegister() just before the sign-up redirect.
            this.showSignupConfirmationIfPending();
            this.getProjectsStatus(returnUrl);
          });
        }
      },
      error: () => {
        this.msalBroadcastService.inProgress$
          .pipe(filter((s: InteractionStatus) => s === InteractionStatus.None), take(1))
          .subscribe(() => {
            if (!this.authService.isAuthenticated)
              this.router.navigate(['/login']).catch(e => console.error('[AppComponent] Login redirect failed:', e));
          });
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        // Adopt a cached account as active if none is set yet. handleRedirectObservable()
        // is the usual place that calls setActiveAccount, but if it resolves null (an MSAL
        // redirect race), the active account stays unset and the bootstrap below never runs
        // — leaving the user on a blank screen with no API calls. Mirror auth.guard's fallback.
        let account = this.msalService.instance.getActiveAccount();
        if (!account) {
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            account = accounts[0];
            this.msalService.instance.setActiveAccount(account);
          }
        }
        const hasSessionToken = !!sessionStorage.getItem('session_token');
        if (account && !hasSessionToken && !this.showConflictOverlay) {
          console.log('[AppComponent] Cached MSAL session detected — registering with backend');
          const userInfo = extractUserInfo(account);
          sessionStorage.setItem('Email', userInfo.email);
          sessionStorage.setItem('UserName', userInfo.fullName || userInfo.displayName);
          sessionStorage.setItem('isLoggedIn', JSON.stringify(true));

          if (!sessionStorage.getItem('access_token')) {
            this.msalService.instance.acquireTokenSilent({ account, scopes: ['openid', 'profile'] })
              .then(r => { 
                if (r?.idToken && getLoginMode() !== 'super-admin') { sessionStorage.setItem('access_token', r.idToken); 
                void this.createNewUser(account.idTokenClaims).then(() => this.showSignupConfirmationIfPending()); 
                }
              })
              .catch(() => { void this.createNewUser(account.idTokenClaims).then(() => this.showSignupConfirmationIfPending()); });
          } else {
           if(getLoginMode() !== 'super-admin') { void this.createNewUser(account.idTokenClaims).then(() => this.showSignupConfirmationIfPending()); }
          }
        } else if (account && hasSessionToken) {
          // Verify session validity with backend to check for cross-device takeover or timeout
          const sessionToken = sessionStorage.getItem('session_token') || '';
          this.rfpService.isSessionValid(sessionToken).subscribe({
            next: (res) => {
              if (res && res.isValid) {
                this.connectHub();
                this.permissionService.setSessionReady(true);
              } else {
                console.warn('[AppComponent] Session is invalid. Reason:', res?.reason);
                if (res?.reason === 'OTHER_DEVICE') {
                  this.handleSessionTerminated('Your session was ended because this account was logged in from another device.');
                } else {
                  this.handleCancel(); // Timeout / standard logout
                }
              }
            },
            error: (err) => {
              // If backend explicitly rejected the session with 401 Unauthorized or 403 Forbidden,
              // the token/session has expired — logout cleanly to prompt a fresh login.
              if (err?.status === 401 || err?.status === 403) {
                console.warn('[AppComponent] Session token expired on backend — logging out for fresh login');
                this.handleCancel();
                return;
              }
              // Connect anyway on transient network error to allow SignalR's built-in retry
              this.connectHub();
              this.permissionService.setSessionReady(true);
            }
          });
        }
      });
    const isRefresh = !!sessionStorage.getItem('_wyber_unloading');
    sessionStorage.removeItem('_wyber_unloading');

    // Clean up any stale closing markers from localStorage
    localStorage.removeItem('_wyber_closing_ts');
    localStorage.removeItem('_wyber_closing_email');
    localStorage.removeItem('_wyber_closing_session');
    localStorage.removeItem('_wyber_closing_access');

    if (isRefresh && sessionStorage.getItem('session_terminated') !== 'true' && sessionStorage.getItem('session_token')) {
      // F5 refresh — backend session is still alive, just reconnect the hub
      console.log('[AppComponent] Page refresh detected — session preserved');
      this.connectHub();
    }
  }

  /**
   * WA-481: shows a one-time "account created" confirmation after a sign-up.
   * ngOnInit sets `wyber_pending_signup` in sessionStorage when the OAuth return
   * carries `state=signup`; here (on the authenticated return) we consume it and
   * surface the confirmation toast. sessionStorage is per-tab and is not copied into
   * newly opened tabs, so the flag — cleared immediately on read — fires exactly once
   * in the tab that completed sign-up, and never on a plain sign-in, refresh, or new tab.
   */
  private showSignupConfirmationIfPending(): void {
    const PENDING_SIGNUP_KEY = 'wyber_pending_signup';
    let isPendingSignup = false;
    try {
      isPendingSignup = sessionStorage.getItem(PENDING_SIGNUP_KEY) === '1';
      if (isPendingSignup) {
        sessionStorage.removeItem(PENDING_SIGNUP_KEY);
      }
    } catch {
      /* storage may be unavailable (private mode) — skip the confirmation */
    }

    if (isPendingSignup) {
      this.toastService.success(
        'Account created successfully',
        'Your registration is complete. Please log in to access the Wyber application.',
        10000
      );
    }
  }

  getProjectsStatus(pendingReturnUrl?: string | null): void {
    this.rfpService.getProjectsStatus().subscribe({
      next: (res) => {
        if (res.data.requiresCompanyProfile) {
          if (pendingReturnUrl && pendingReturnUrl !== '/') {
            setReturnUrl(pendingReturnUrl);
          }
          this.router.navigate(['/profile'], { queryParams: { tab: 'company', returnUrl: pendingReturnUrl || '/' }, replaceUrl: true });
        } else {
          const stashedUrl = consumeReturnUrl();
          const returnUrl = pendingReturnUrl || stashedUrl || '/';
          this.router.navigateByUrl(returnUrl).catch(err => console.error('[AppComponent] Navigation error:', err));
        }
      },
      error: (err) => {
        console.error('[AppComponent] getProjectsStatus error:', err);
        const stashedUrl = consumeReturnUrl();
        const returnUrl = pendingReturnUrl || stashedUrl || '/';
        this.router.navigateByUrl(returnUrl).catch(err => console.error('[AppComponent] Navigation error:', err));
      },
    });
  }

   createNewSuperAdminUser(details: any) {
    const userInfo = extractUserInfo({ idTokenClaims: details });
    const payload = {
      email: userInfo.email,
      firstName: userInfo.fullName,
      lastName: userInfo.initials
    };
    this.rfpService.createSuperAdminUser(payload).subscribe({
      next: (res) => {
        const token = res?.SessionToken ?? res?.sessionToken ?? res?.data?.sessionToken;
        if (token) {
          sessionStorage.setItem('session_token', token);
          sessionStorage.setItem('_sessionLoginTime', Date.now().toString());
        }
        this.cdr.detectChanges();
        // this.connectHub(); // connects SignalR + claims tab leadership
      },
      error: (err) => {
        // Fallback for older backend that still returns 409 for the same case.
        if (err.status === 409) { 

        } else {
          console.error('[AppComponent] Error creating user:', err);
        }
      }
    });
  }



  // ── Session / user creation ─────────────────────────────────────────────────

  private createNewUserPromise: Promise<void> | null = null;

  async createNewUser(details: any, forceLogin: boolean = false): Promise<void> {
    if (!forceLogin && this.createNewUserPromise) {
      console.log('[AppComponent] createNewUser: Reusing in-flight createNewUser request');
      return this.createNewUserPromise;
    }

    const execute = async (): Promise<void> => {
      const userInfo = extractUserInfo({ idTokenClaims: details });
      const payload = {
        email: userInfo.email,
        firstName: userInfo.givenName || userInfo.fullName || userInfo.displayName,
        lastName: userInfo.surname,
        forceLogin,
        deviceInfo: this.deviceInfo,
        ipAddress: this.deviceId || this.ipAddress,
      };

      await new Promise<void>((resolve) => {
        this.rfpService.createNewUser(payload).subscribe({
          next: (res) => {
            // Active session elsewhere: backend now returns 200 OK with requiresConfirmation
            // (previously a 409). ASP.NET serializes JSON camelCase, but tolerate both casings.
            // Show the "USE HERE" confirmation instead of proceeding — no session token here.
            if (res?.requiresConfirmation === true || res?.RequiresConfirmation === true ||
                res?.code === 'ALREADY_LOGGED_IN' || res?.Code === 'ALREADY_LOGGED_IN') {
              this.pendingUserDetails = details;
              this.showConflictOverlay = true;
              this.cdr.detectChanges();
              resolve();
              return;
            }
            const token = res?.SessionToken ?? res?.sessionToken ?? res?.data?.sessionToken;
            if (token) {
              sessionStorage.setItem('session_token', token);
              sessionStorage.setItem('_sessionLoginTime', Date.now().toString());
            }
            const tenantId = res?.tenantId ?? res?.tenant_id ?? res?.data?.tenantId ?? res?.data?.tenant_id;
            if (tenantId) {
              sessionStorage.setItem('tenant_id', String(tenantId));
            }
            const userId = res?.UserId ?? res?.userId ?? res?.data?.UserId ?? res?.data?.userId;
            if (userId) {
              sessionStorage.setItem('user_id', String(userId));
              sessionStorage.setItem('userId', String(userId));
            }
            this.showConflictOverlay = false;
            this.cdr.detectChanges();
            this.connectHub(); // connects SignalR + claims tab leadership
            this.permissionService.setSessionReady(true);
            resolve();
          },
          error: (err) => {
            // Fallback for older backend that still returns 409 for the same case.
            if (err.status === 409) {
              this.pendingUserDetails = details;
              this.showConflictOverlay = true;
              this.cdr.detectChanges();
            } else {
              console.error('[AppComponent] Error creating user:', err);

              // A *transient* failure here (network blip / 5xx) previously left the app with no
              // session-ready signal at all: the profile + ui-access bootstrap never ran, so the
              // permission context stayed empty and every gated control rendered disabled until
              // a manual refresh. The MSAL token is still valid, so continue the bootstrap —
              // this mirrors the fallback the isSessionValid network-error branch already takes.
              // The POST is deliberately NOT retried: it registers a session, and a retry after
              // a lost response would re-trigger the "already logged in" conflict.
              //
              // Deterministic failures (401 unauthorized, 403 pending-approval/rejected) fall
              // through untouched — those states own their own screens and must not bootstrap.
              if (isTransientHttpError(err)) {
                this.connectHub(); // idempotent: startConnection() no-ops if already connected
                this.permissionService.setSessionReady(true);
              }
            }
            resolve();
          }
        });
      });
    };

    if (forceLogin) {
      return execute();
    }

    this.createNewUserPromise = execute().finally(() => {
      this.createNewUserPromise = null;
    });

    return this.createNewUserPromise;
  }

  handleUseHere(): void {
    if (this.useHereLoading) return;

    this.useHereLoading = true;
    this.useHereError = '';
    this.cdr.detectChanges();

    if (!this.pendingUserDetails) {
      this.useHereLoading = false;
      this.useHereError = 'Session details lost. Please logout and login again.';
      this.cdr.detectChanges();
      return;
    }

    const userInfo = extractUserInfo({ idTokenClaims: this.pendingUserDetails });
    const email = userInfo?.email ?? sessionStorage.getItem('Email') ?? '';

    const payload = {
      email,
      firstName: userInfo.givenName,
      lastName: userInfo.surname,
      forceLogin: true,
      deviceInfo: this.deviceInfo,
      ipAddress: this.deviceId || this.ipAddress,
    };

    this.rfpService.createNewUser(payload).subscribe({
      next: async (res) => {
        const token = res?.sessionToken ?? res?.SessionToken ?? res?.data?.sessionToken;
        if (token) {
          sessionStorage.setItem('session_token', token);
          sessionStorage.setItem('_sessionLoginTime', Date.now().toString());
        }
        const tenantId = res?.tenantId ?? res?.tenant_id ?? res?.data?.tenantId ?? res?.data?.tenant_id;
        if (tenantId) {
          sessionStorage.setItem('tenant_id', String(tenantId));
        }
        const userId = res?.UserId ?? res?.userId ?? res?.data?.UserId ?? res?.data?.userId;
        if (userId) {
          sessionStorage.setItem('user_id', String(userId));
          sessionStorage.setItem('userId', String(userId));
        }

        // Notify other same-browser tabs of the cross-browser takeover
        try {
          this.sessionChannel?.postMessage({
            type: 'SESSION_TAKEOVER',
            newTabId: this.tabSessionId,
            email,
            timestamp: Date.now(),
          });
        } catch (e) {
          console.warn('[AppComponent] BroadcastChannel postMessage failed:', e);
        }

        // Silently refresh JWT
        try {
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            const r = await this.msalService.instance.acquireTokenSilent({ account: accounts[0], scopes: ['openid', 'profile'] });
            if (r?.idToken) sessionStorage.setItem('access_token', r.idToken);
          }
        } catch (e) {
          console.warn('[AppComponent] Silent token refresh failed (non-critical):', e);
        }

        this.useHereLoading = false;
        this.useHereError = '';
        this.showConflictOverlay = false;
        this.showTerminatedOverlay = false;
        this.cdr.detectChanges();

        this.connectHub(); // connects SignalR + claims tab leadership
        this.permissionService.setSessionReady(true);
      },
      error: (err) => {
        this.useHereLoading = false;
        if      (err?.status === 409) this.useHereError = 'Could not take over session. Please try again.';
        else if (err?.status === 0)   this.useHereError = 'Cannot reach the server. Check your connection.';
        else                          this.useHereError = 'Something went wrong. Please logout and try again.';
        this.cdr.detectChanges();
      }
    });
  }

  handleCancel(): void {
    this.stopGracePeriod();
    this.showGraceWarning = false;
    sessionStorage.removeItem('session_terminated');
    sessionStorage.removeItem('session_terminated_reason');
    this.showConflictOverlay  = false;
    this.showTerminatedOverlay = false;
    this.showOtherTabOverlay  = false;
    this.sessionRealtimeService.stopConnection();
    this.authService.logout();
  }

  dismissTerminatedOverlay(): void {
    this.stopGracePeriod();
    this.showGraceWarning = false;
    this.showTerminatedOverlay = false;
    this.terminatedMessage = '';
    sessionStorage.removeItem('session_terminated');
    sessionStorage.removeItem('session_terminated_reason');
    this.cdr.detectChanges();
    this.authService.logout(true);
  }

  completeForcedLogout(): void {
    this.stopGracePeriod();
    this.showGraceWarning = false;
    sessionStorage.setItem('session_terminated', 'true');
    this.showTerminatedOverlay = true;
    this.cdr.detectChanges();
  }

  private updateGraceBannerBodyClass(active: boolean): void {
    if (typeof document === 'undefined') return;
    if (active) {
      document.body.classList.add('has-session-grace-banner');
      document.documentElement.style.setProperty('--grace-banner-height', '42px');
    } else {
      document.body.classList.remove('has-session-grace-banner');
      document.documentElement.style.setProperty('--grace-banner-height', '0px');
    }
  }

  private stopGracePeriod(): void {
    if (this.graceInterval !== null) {
      clearInterval(this.graceInterval);
      this.graceInterval = null;
    }
    this.updateGraceBannerBodyClass(false);
  }

  private startGracePeriod(): void {
    this.stopGracePeriod();
    this.graceCountdown = 60;
    this.showGraceWarning = true;
    this.updateGraceBannerBodyClass(true);
    this.cdr.detectChanges();

    this.graceInterval = setInterval(() => {
      this.graceCountdown--;
      if (this.graceCountdown <= 0) {
        this.completeForcedLogout();
      } else {
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  private handleSessionTerminated(reason?: string): void {
    const msg = reason || 'Your session was ended because this account was logged in from another device.';
    console.warn('[AppComponent] Session conflict from another device. Starting grace period:', msg);
    this.terminatedMessage = msg;
    sessionStorage.setItem('session_terminated_reason', msg);
    this.sessionRealtimeService.stopConnection();
    this.showConflictOverlay = false;
    this.showOtherTabOverlay = false;

    // Start 60-second grace period warning before force termination
    this.startGracePeriod();
  }

  /** Connect SignalR hub and claim same-browser tab leadership. Single call-site. */
  private connectHub(): void {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      // No token yet — try a silent refresh first, then connect
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance
          .acquireTokenSilent({ account: accounts[0], scopes: ['openid', 'profile'] })
          .then((result) => {
            if (result?.idToken) {
              sessionStorage.setItem('access_token', result.idToken);
            }
            this.doConnectHub();
          })
          .catch(() => {
            // Can't get a token — connect anyway (will fail gracefully)
            this.doConnectHub();
          });
      }
      return;
    }
    this.doConnectHub();
  }

  private doConnectHub(): void {
    this.sessionRealtimeService.startConnection((data) => {
      console.warn('[AppComponent] ForceLogout pushed from server:', data);
      const reason = data?.reason || 'Your session was ended because this account was logged in from another device.';
      this.handleSessionTerminated(reason);
    });
    this.claimTabLeadership();
    this.scheduleTokenRefresh();
    this.startIdleTimer();
    if(getLoginMode() !== 'super-admin') {
      this.uiAccessService.loadAccessConfig().subscribe();
    }
  }

  /**
   * Proactively refreshes the MSAL access_token in the background.
   * Runs every 5 minutes to ensure the token is always fresh.
   * This prevents 401s from ever reaching the user.
   */
  private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;

  private scheduleTokenRefresh(): void {
    // Idempotent: connectHub() can fire repeatedly (reconnects, tab leadership,
    // hub restarts), and restarting the interval each time would keep pushing
    // back the recurring refresh — so only start it once per session.
    if (this.tokenRefreshTimer) return;
    // Run every 30 minutes to keep token fresh in background
    this.tokenRefreshTimer = setInterval(() => this.doSilentTokenRefresh(), 30 * 60 * 1000);
    // Also run once immediately to ensure token is fresh right now
    this.doSilentTokenRefresh();
  }

  private doSilentTokenRefresh(): void {
    // Don't refresh token if user has been idle — let the idle timer handle logout
    const idleTime = Date.now() - this.lastActivityTime;
    if (idleTime >= this.IDLE_TIMEOUT_MS) {
      return;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (!accounts.length) return;

    // Wait for MSAL to be fully initialized and idle before refreshing
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)
      )
      .subscribe(() => {
        this.msalService.instance
          .acquireTokenSilent({ account: accounts[0], scopes: ['openid', 'profile'] })
          .then((result) => {
            if (result?.idToken) {
              sessionStorage.setItem('access_token', result.idToken);
            }
          })
          .catch((err) => {
            console.warn('[AppComponent] Background token refresh failed:', err);
          });
      });
  }

  // ── Idle Timeout: Auto-logout after 30 minutes of inactivity ──────────────

  private readonly IDLE_TIMEOUT_MS = 120 * 60 * 1000; // 2 hours
  private lastActivityTime = Date.now();
  private idleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private readonly activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
  private activityHandler = this.throttle(() => { this.lastActivityTime = Date.now(); }, 10000);

  private startIdleTimer(): void {
    // Listen for user activity
    this.activityEvents.forEach(event =>
      document.addEventListener(event, this.activityHandler, { passive: true })
    );

    // Check every 60 seconds if user has been idle too long
    this.idleCheckInterval = setInterval(() => {
      this.checkIdleAndLogout();
    }, 60000);

    // When laptop is locked/unlocked or tab is hidden/shown, the interval may not fire.
    // Use visibilitychange to catch up when the user returns.
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      // Tab just became visible (user unlocked laptop or switched back to tab)
      this.checkIdleAndLogout();
    }
  };

  private checkIdleAndLogout(): void {
    const idleTime = Date.now() - this.lastActivityTime;
    if (idleTime >= this.IDLE_TIMEOUT_MS && this.authService.isAuthenticated) {
      console.log('[AppComponent] User idle for 30 minutes — logging out');
      const myEmail: any = sessionStorage.getItem('Email');
      localStorage.setItem('last_lagged_user', myEmail);
      this.stopIdleTimer();
      this.authService.logout();
    }
  }

  private stopIdleTimer(): void {
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }
    this.activityEvents.forEach(event =>
      document.removeEventListener(event, this.activityHandler)
    );
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  /** Simple throttle — only invokes fn once per limitMs regardless of how often it's called */
  private throttle(fn: () => void, limitMs: number): () => void {
    let lastCall = 0;
    return () => {
      const now = Date.now();
      if (now - lastCall >= limitMs) {
        lastCall = now;
        fn();
      }
    };
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.sessionChannel?.close();
    this.stopTabHeartbeat();
    this.stopHeartbeatMonitor();
    this.sessionRealtimeService.stopConnection();
    this.stopGracePeriod();
    if (this.tokenRefreshTimer) clearInterval(this.tokenRefreshTimer);
    this.stopIdleTimer();
  }
}
